import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getPostfixWithRobotId, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { changeEndpointId } from "./sesItemConfigSlice";
import { ENDPOINTS_SES, URL_SES } from "@/constants/api_endpoints";
import { IAddEndpointProps, IAddingEndpointResponse, IDeleteEndpointProps, IEditEndpointProps, ISesEndpointState } from "@/types/ses/sesEndpointTypes";
import { IEndpoint } from "@/types/ses/sesItemConfigTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: ISesEndpointState = {
	endpointList: {
		data: [],
		dictionary: {},
		variableNames: [],
		variableValues: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		activeEndpointId: "",
		backgroundLoading: false,
	},
	addingEndpoint: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	editingEndpoint: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingEndpoint: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// список КТ
export const getEndpointList = createAsyncThunk("sesEndpoint/list", async (robotId: string, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IEndpoint[] | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.ENDPOINT_LIST}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление КТ
export const addEndpoint = createAsyncThunk("sesEndpoint/add", async ({ robotId, data }: IAddEndpointProps, { dispatch, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddingEndpointResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.ENDPOINT_ADD}/${robotId}`, {
			data: JSON.stringify(data),
		});
		if (response.data.id) {
			dispatch(changeEndpointId(response.data.id));
		}
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение КТ
export const editEndpoint = createAsyncThunk("sesEndpoint/edit", async ({ robotId, endpointId, data }: IEditEndpointProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_SES}/${ENDPOINTS_SES.ENDPOINT_EDIT}/${robotId}/${endpointId}`, {
			data: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление КТ
export const deleteEndpoint = createAsyncThunk("sesEndpoint/delete", async ({ robotId, endpointId }: IDeleteEndpointProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_SES}/${ENDPOINTS_SES.ENDPOINT_DELETE}/${robotId}/${endpointId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesEndpointSlice = createSlice({
	name: "sesEndpoint",
	initialState,
	reducers: {
		// изменение флага фоновой загрузки списка КТ
		changeBackgroundDownloadEndpointList: (state, action: PayloadAction<boolean>) => {
			state.endpointList.backgroundLoading = action.payload;
		},
		// -------------------------------------------------------------------------
		// добавление активной КТ
		addActiveEndpoint: (state, action: PayloadAction<string>) => {
			state.endpointList.activeEndpointId = action.payload;
		},
		// добавление названий и значений переменных КТ
		addVariableNamesAndValues: (state, action: PayloadAction<{ variableNames: string[]; variableValues: string[] }>) => {
			state.endpointList.variableNames = action.payload.variableNames;
			state.endpointList.variableValues = action.payload.variableValues;
		},
		// -------------------------------------------------------------------------
		// очистка state
		clearState: (state) => {
			state.endpointList = initialState.endpointList;
			state.addingEndpoint = initialState.addingEndpoint;
			state.editingEndpoint = initialState.editingEndpoint;
			state.deletingEndpoint = initialState.deletingEndpoint;
		},
		// очистка списка КТ
		clearEndpointList: (state) => {
			state.endpointList = initialState.endpointList;
		},
		// очистка статуса добавления КТ
		clearAddingEndpoint: (state) => {
			state.addingEndpoint = initialState.addingEndpoint;
		},
		// очистка статуса изменения КТ
		clearEditingEndpoint: (state) => {
			state.editingEndpoint = initialState.editingEndpoint;
		},
		// очистка статуса удаления КТ
		clearDeletingEndpoint: (state) => {
			state.deletingEndpoint = initialState.deletingEndpoint;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getEndpointList.pending, (state) => {
				state.endpointList.status = RequestStatus.LOADING;
			})
			.addCase(getEndpointList.fulfilled, (state, action) => {
				state.endpointList.backgroundLoading = false; // сброс флага фоновой загрузки
				if (Array.isArray(action.payload)) {
					state.endpointList.status = RequestStatus.IDLE;
					state.endpointList.data = action.payload.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
					const dictionary: Record<string, string> = {}; // создаем словарь id-name
					action.payload.forEach((endpointElem) => (dictionary[endpointElem.id] = endpointElem.name));
					state.endpointList.dictionary = dictionary;
				} else state.endpointList.status = RequestStatus.FAILED;
			})
			.addCase(getEndpointList.rejected, (state, action: PayloadAction<unknown>) => {
				state.endpointList.backgroundLoading = false; // сброс флага фоновой загрузки
				state.endpointList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.endpointList.error = error;
				state.endpointList.message = message;
			})
			.addCase(addEndpoint.pending, (state) => {
				state.addingEndpoint.status = RequestStatus.LOADING;
			})
			.addCase(addEndpoint.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingEndpoint.status = RequestStatus.IDLE;
					state.addingEndpoint.error = action.payload.error;
					state.addingEndpoint.message = action.payload.message;
				} else state.addingEndpoint.status = RequestStatus.FAILED;
			})
			.addCase(addEndpoint.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingEndpoint.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingEndpoint.error = error;
				state.addingEndpoint.message = message;
			})
			.addCase(editEndpoint.pending, (state) => {
				state.editingEndpoint.status = RequestStatus.LOADING;
			})
			.addCase(editEndpoint.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingEndpoint.status = RequestStatus.IDLE;
					state.editingEndpoint.error = action.payload.error;
					state.editingEndpoint.message = action.payload.message;
				} else state.editingEndpoint.status = RequestStatus.FAILED;
			})
			.addCase(editEndpoint.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingEndpoint.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingEndpoint.error = error;
				state.editingEndpoint.message = message;
			})
			.addCase(deleteEndpoint.pending, (state) => {
				state.deletingEndpoint.status = RequestStatus.LOADING;
			})
			.addCase(deleteEndpoint.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingEndpoint.status = RequestStatus.IDLE;
					state.deletingEndpoint.error = action.payload.error;
					state.deletingEndpoint.message = action.payload.message;
				} else state.deletingEndpoint.status = RequestStatus.FAILED;
			})
			.addCase(deleteEndpoint.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingEndpoint.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingEndpoint.error = error;
				state.deletingEndpoint.message = message;
			});
	},
});

export const {
	changeBackgroundDownloadEndpointList,
	addActiveEndpoint,
	addVariableNamesAndValues,
	clearState,
	clearEndpointList,
	clearAddingEndpoint,
	clearEditingEndpoint,
	clearDeletingEndpoint,
} = sesEndpointSlice.actions;

export const selectEndpointList = (state: RootState) => state.sesEndpoint.endpointList;
export const selectActiveEndpointId = (state: RootState) => state.sesEndpoint.endpointList.activeEndpointId;
export const selectAddingEndpoint = (state: RootState) => state.sesEndpoint.addingEndpoint;
export const selectEditingEndpoint = (state: RootState) => state.sesEndpoint.editingEndpoint;
export const selectDeletingEndpoint = (state: RootState) => state.sesEndpoint.deletingEndpoint;

export default sesEndpointSlice.reducer;
